import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Button,
  Badge,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  useDisclosure,
  HStack,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { FiEdit2, FiTrash2, FiPlus } from 'react-icons/fi';

const API_BASE = '/api';

function Refunds() {
  const toast = useToast();
  const [refunds, setRefunds] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRefund, setEditingRefund] = useState(null);
  const [refundForm, setRefundForm] = useState({ payment_id: '', amount: '', status: 'pending', reason: '' });
  
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [refundToDelete, setRefundToDelete] = useState(null);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
  });

  const fetchData = () => {
    Promise.all([
      fetch(`${API_BASE}/refunds/`, { headers: getAuthHeaders() }).then(r => r.json()),
      fetch(`${API_BASE}/payments/`, { headers: getAuthHeaders() }).then(r => r.json()),
    ]).then(([refs, pays]) => {
      setRefunds(Array.isArray(refs) ? refs : []);
      setPayments(Array.isArray(pays) ? pays : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openAddRefund = () => {
    setEditingRefund(null);
    setRefundForm({ payment_id: '', amount: '', status: 'pending', reason: '' });
    onModalOpen();
  };

  const openEditRefund = (refund) => {
    setEditingRefund(refund);
    setRefundForm({ 
      payment_id: refund.payment_id, 
      amount: refund.amount,
      status: refund.status,
      reason: refund.reason || ''
    });
    onModalOpen();
  };

  const handleSaveRefund = async () => {
    const method = editingRefund ? 'PUT' : 'POST';
    const url = editingRefund 
      ? `${API_BASE}/refunds/${editingRefund.id}/` 
      : `${API_BASE}/refunds/`;
    
    const payload = { ...refundForm };
    if (!editingRefund) {
      payload.amount = parseInt(refundForm.amount) || 0;
    }
    
    try {
      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) {
        fetchData();
        toast({ title: editingRefund ? 'Refund updated' : 'Refund created', status: 'success', duration: 3000 });
        onModalClose();
      } else {
        toast({ title: data.error || 'Error saving refund', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error saving refund', status: 'error', duration: 3000 });
    }
  };

  const confirmDelete = (refund) => {
    setRefundToDelete(refund);
    onDeleteOpen();
  };

  const handleDelete = async () => {
    if (!refundToDelete) return;
    try {
      const res = await fetch(`${API_BASE}/refunds/${refundToDelete.id}/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        fetchData();
        toast({ title: 'Refund deleted', status: 'success', duration: 3000 });
      } else {
        toast({ title: 'Error deleting refund', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error deleting refund', status: 'error', duration: 3000 });
    }
    onDeleteClose();
    setRefundToDelete(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'succeeded': return 'green';
      case 'failed': return 'red';
      case 'canceled': return 'gray';
      default: return 'yellow';
    }
  };

  if (loading) {
    return <Box textAlign="center" py={20}><Spinner size="xl" color="teal.500" /></Box>;
  }

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <Heading size="lg">Refunds</Heading>
        <Button leftIcon={<FiPlus />} colorScheme="teal" onClick={openAddRefund}>
          Add Refund
        </Button>
      </HStack>

      <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Customer</Th>
              <Th>Payment Stripe ID</Th>
              <Th>Refund Stripe ID</Th>
              <Th>Amount</Th>
              <Th>Status</Th>
              <Th>Reason</Th>
              <Th>Created</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {refunds.map(refund => (
              <Tr key={refund.id}>
                <Td fontWeight="medium">{refund.customer_email}</Td>
                <Td><Text fontSize="sm" fontFamily="mono">{refund.payment_stripe_id}</Text></Td>
                <Td><Text fontSize="sm" fontFamily="mono">{refund.stripe_id}</Text></Td>
                <Td>{(refund.amount / 100).toFixed(2)}</Td>
                <Td>
                  <Badge colorScheme={getStatusColor(refund.status)}>
                    {refund.status}
                  </Badge>
                </Td>
                <Td>{refund.reason || '-'}</Td>
                <Td>{new Date(refund.created_at).toLocaleDateString()}</Td>
                <Td>
                  <HStack spacing={2}>
                    <IconButton
                      icon={<FiEdit2 />}
                      size="sm"
                      variant="ghost"
                      onClick={() => openEditRefund(refund)}
                      aria-label="Edit"
                    />
                    <IconButton
                      icon={<FiTrash2 />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => confirmDelete(refund)}
                      aria-label="Delete"
                    />
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        {refunds.length === 0 && (
          <Text color="gray.500" textAlign="center" py={8}>No refunds found</Text>
        )}
      </Box>

      {/* Add/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={onModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingRefund ? 'Edit Refund' : 'Add Refund'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {!editingRefund && (
              <>
                <FormControl mb={4}>
                  <FormLabel>Payment</FormLabel>
                  <Select
                    value={refundForm.payment_id}
                    onChange={(e) => setRefundForm({ ...refundForm, payment_id: e.target.value })}
                    placeholder="Select payment"
                  >
                    {payments.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.customer_email} - {(p.amount / 100).toFixed(2)} {p.currency.toUpperCase()} - {p.stripe_id}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>Amount (in cents)</FormLabel>
                  <Input
                    type="number"
                    value={refundForm.amount}
                    onChange={(e) => setRefundForm({ ...refundForm, amount: e.target.value })}
                    placeholder="1000"
                  />
                </FormControl>
              </>
            )}
            <FormControl mb={4}>
              <FormLabel>Status</FormLabel>
              <Select
                value={refundForm.status}
                onChange={(e) => setRefundForm({ ...refundForm, status: e.target.value })}
              >
                <option value="pending">Pending</option>
                <option value="succeeded">Succeeded</option>
                <option value="failed">Failed</option>
                <option value="canceled">Canceled</option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Reason</FormLabel>
              <Input
                value={refundForm.reason}
                onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })}
                placeholder="Reason for refund"
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onModalClose}>Cancel</Button>
            <Button colorScheme="teal" onClick={handleSaveRefund}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Refund</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Are you sure you want to delete this refund? This action cannot be undone.
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteClose}>Cancel</Button>
            <Button colorScheme="red" onClick={handleDelete}>Delete</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default Refunds;
